import type { Product } from 'schema-dts';

import { Advantage } from '../advantages.types';
import { DeliveryPromise, DeliveryPromiseExtendedResponseObject } from '../DeliveryPromise';
import { ProductLinkType, ProductSpecialtyType, PDPVersion } from '../enums';
import { ExpertReview } from '../ExpertReviewTypes';
import { SupportedLanguages } from '../GeneralTypes';
import { ImagePathWithRenditions } from '../ImageTypes';
import { ProductColor, ProductLister } from '../product-lister';

import { ProductAvailability } from './ProductAvailabilityTypes';
import { ProductManual } from './ProductManualTypes';
import { ProductPlacementCopyColorPosition } from './ProductPlacementCopyTypes';
import {
  FinanceProvider,
  ProductDetailPriceData,
  ProductFulfillment,
  ProductPrice,
  ProductPriceOverlay,
  ProductPrices,
} from './ProductPriceTypes';
import { ProductVideo } from './ProductVideoTypes';

export type ProductDetail<ReviewQueryType = unknown> = {
  active?: boolean;
  brand?: ProductBrand;
  brandId?: string;
  brandQualifier?: string;
  buyingGroup: string;
  careFeature?: string;
  category?: ProductCategory;
  colourAvailability?: number;
  defaultCategoryId?: string;
  defaultColor?: number;
  defaultImage?: string;
  deliveryPromise?: DeliveryPromise;
  deliveryPromiseExtended?: DeliveryPromiseExtendedResponseObject;
  gender?: string;
  genderFeatureId?: string;
  fictionalId?: string;
  mainWebShop?: string;
  productAvailability?: number;
  productCode: string;
  productColorVariations?: Array<ProductColorVariation>;
  productId: string;
  productQualifier?: string;
  productTranslatedProperties?: ProductTranslatedProperty &
    Partial<Record<SupportedLanguages, ProductTranslatedProperty>>;
  productTypeFeatureId?: string;
  selectedColorId?: number;
  selectedSku?: string;
  seoUrl: string;
  showColorSwatches?: boolean;
  showQuantity?: boolean;
  showSizeGuide?: boolean;
  thumbSplit?: boolean;
  skuAvailability?: number;
  visible?: boolean;
  webOnly?: boolean;
  unavailableOnline?: boolean;
  productDiscountPercentage?: number;
  productDiscountAmount?: number;
  fulfillment?: ProductFulfillment;
  productReview?: ReviewQueryType;
  sustainable?: boolean;
  ourPlanet?: boolean;
  productTechnicalCategories?: Array<ProductTechnicalCategory>;
  selectedProductTab?: string | null;
  expertReviews?: Array<ExpertReview>;
  productVideos?: Array<ProductVideo>;
  productContentVideos?: Array<ProductVideo>;
  linkedProducts?: Array<ProductLister>;
  mainWebshopId?: number;
  productCare?: ProductCareData;
  sizeGuideContent?: string;
  sizeGuideContentUrl?: string;
  sizeGuideViewed?: boolean;
  pdpVersion?: PDPVersion;
  alternativeProducts?: Array<ProductLister>;
  productVariants?: Array<ProductVariant>;
  productFeatures?: Array<ProductFeature>;
  buyingGroupLevel3Code?: string;
  specialtyType?: ProductSpecialtyType;
  sizeCombinationNotExist?: boolean;
  additionalProductSelector?: ProductAdditionalProductSelector;
  productCertificates?: ProductSustainableCertificationProps;
};

export type ProductBrand = {
  description: string;
  logo: string;
  relativePath: string;
  logoTimestamp?: number;
};

export type ProductColorVariation = {
  active: boolean;
  alwaysVisible?: boolean;
  availability: number;
  ccExcluded?: boolean;
  color: string;
  colorId: number;
  colorCode: string;
  defaultImage?: string;
  hasDifferentPrices?: boolean;
  hasDiscount?: boolean;
  images: Array<ProductColorVariationImage>;
  markdown?: boolean;
  priceOverlay?: ProductPriceOverlay;
  prices?: ProductPrices;
  swatch: string;
  sizes: Array<ProductColorVariationSize>;
  translations: ProductColorVariationTranslation;
  visible: boolean;
  webOnly: boolean;
  colourDiscountPercentage: number;
  colourDiscountAmount: number;
  productPlacementCopyPositions?: Array<ProductPlacementCopyColorPosition>;
  financeProviders?: Array<FinanceProvider>;
  newProduct?: boolean;
  productColorId?: number;
};

export type ProductPriceInformation = {
  hasDiscount: boolean;
  prices: ProductPrices;
  bundleDiscount?: number;
};

export type ProductDynamicSavingsInformation = {
  productDiscountPercentage: number;
  productDiscountAmount: number;
};

export type ProductPriceInformationWithDiscount = ProductPriceInformation & ProductDynamicSavingsInformation;

export type ProductColorVariationTranslation = {
  language: string;
  description: string;
  modelSize: string | null;
  modelHeight: string | null;
  sizingAdvice: string | null;
};

export type ProductColorVariationImage = {
  picture: string;
  pictureType: string;
  sortOrder: number;
  thumbnailImageUrl?: string;
};

export type ProductColorVariationSize = {
  active?: boolean;
  availability?: number;
  code: string;
  commercialSize?: string | null;
  commercialSizeDefault?: string | null;
  commercialSizePrefix?: string | null;
  commercialSizeSuffix?: string | null;
  internalSizePrefix?: string | null;
  internalSizeSuffix?: string | null;
  fullCommercialSize?: string | null;
  lastPieces?: boolean;
  exactAvailability?: null | number;
  order: number;
  originalSize?: string;
  prices?: ProductPrices;
  sku: string;
  productDiscountPercentage?: number | null;
  productDiscountAmount?: number | null;
  hasDiscount?: boolean;
  storeSourceExcluded?: boolean;
  fulfillmentCopy?: Record<string, string> | null;
  historicAvailable?: boolean;
};

export type ProductTranslatedProperty = {
  brand: string;
  description?: string;
  features: string;
  gender: string;
  information: string;
  language: string;
  seoUrl: string;
  technicalDescription: string;
  title: string;
  modelSize: string;
  modelHeight: string;
  sizingAdvice: string;
};

export type ProductEsiData = {
  productPrice?: ProductPrice;
  productAvailability?: ProductAvailability;
};
export type ProductCategory = {
  absoluteUrl: string;
  active: boolean;
  categoryId: string;
  children: Array<Record<string, unknown>>;
  description: string;
  icon: string;
  linkType: string;
  parentCategoryId: string;
  relativeUrl: string;
  sortOrder: number;
  subLevels: boolean;
  visible: boolean;
};

export type BasketPopupRecommendations = {
  title?: string;
  schemeName?: string; // to remove
  actionId?: string;
};

export type DeliveryPromiseExplanations = {
  storeStock?: string;
  warehouseStock?: string;
  supplierStock?: string;
  preOrder?: string;
  postOut?: string;
  path: string;
};

export type ProductDeliveryPromiseExplanations = DeliveryPromiseExplanations & {
  advantages?: Array<Advantage>;
  basketPopUp?: BasketPopupRecommendations;
  klarna?: boolean;
};

export type ProductTileVerticalFormat = 'simple' | 'normal' | 'extensive';

export enum ProductDetailTabTypes {
  SPECIFICATIONS = 'specifications',
  REVIEWS = 'reviews',
  DESCRIPTION = 'description',
  FEATURE_CATEGORIES = 'featureCategories',
  PRODUCT_CARE = 'productCare',
  OUR_PLANET = 'ourPlanet',
}

export enum ProductDetailReviewType {
  USER = 'user',
  EXPERT = 'expert',
}

export type ProductDetailTabReviewType = {
  type: ProductDetailReviewType;
  orderNumber: number;
};

export type ProductDetailTabOurPlanetFeatures = {
  id: string;
  description: string;
  title: string;
};

export type ProductDetailTabOurPlanet = {
  ourPlanetFeatures?: Array<ProductDetailTabOurPlanetFeatures>;
  ppcImageUrl?: string;
  ppcAltText?: string;
  ppcDescription?: string;
  readMoreText?: string;
  readMoreUrl?: string;
};

export type ProductDetailTab = ProductDetailTabOurPlanet & {
  type: ProductDetailTabTypes;
  shouldDisplay?: boolean;
  showCertInTab?: boolean;
  title?: string;
  showTable?: boolean;
  showExplanation?: boolean;
  showEndUserReview?: boolean;
  expertReviewTitleOverride?: string;
  reviewTypes?: Array<ProductDetailTabReviewType>;
  expertReviewPath?: string;
  reviewTitle?: string;
  excludedCategories?: Array<ExcludedCategory>;
  includedCategories?: Array<IncludedCategory>;
  path?: string;
};

export type ExcludedCategory = {
  id: string;
};

export type IncludedCategory = {
  id: string;
};

export type ShopTheLookBlock = {
  defaultTitle?: string;
  enableShopTheLook: boolean;
  flyoutTitle?: string;
  linkLabel?: string;
};

export type BundleDealsBlock = {
  enableBundleDeals: boolean;
  genericPromoText?: string;
  title?: string;
  useMonetate: boolean;
  monetateActionIds?: string;
};

export enum ProductCareMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export enum ProductCareVideoType {
  EMBEDDED_LINK = 'embeddedLink',
  DAM = 'damAsset',
}

export type ProductCareVideo = {
  allowFullscreen?: boolean;
  embeddedLink?: string;
  videoPath?: string;
  autoplay?: boolean;
  loop?: boolean;
  type: ProductCareVideoType;
};

export type ProductCareCTA = {
  alt?: string;
  label?: string;
  linkPath?: string;
  targetIsBlank: boolean;
  buttonVariant?: string;
  horizontalAlign?: string;
};

export type ProductCareData = {
  cta1?: ProductCareCTA;
  cta2?: ProductCareCTA;
  media?: {
    description?: string;
    textIconColor?: string;
    type: ProductCareMediaType;
    video?: ProductCareVideo;
    image?: ImagePathWithRenditions;
    imageLinkPath?: string;
  };
  products?: Array<{
    productCode: string;
    productColor: string;
    fallbackProducts?: Array<{
      productCode: string;
      productColor: string;
    }>;
  }>;
  description1?: string;
  description2?: string;
  description3?: string;
  title: string;
  subtitle?: string;
  tileFormat?: string;
  productsData?: Array<ProductLister>;
};

export type ProductDetailV2Options = {
  productCode: string;
  colour: string;
  deliveryPromiseExplanations: DeliveryPromiseExplanations;
  advantages?: Array<Advantage>;
  basketPopUp?: BasketPopupRecommendations;
  tabs: Array<ProductDetailTab>;
  showTabsAsAccordion?: boolean;
  shopTheLookBlock: ShopTheLookBlock;
  bundleDealsBlock: BundleDealsBlock;
  alternatives?: {
    campaign?: string;
    campaignFallback?: string;
  };
};

export type ProductDetailFreeDeliveryMessageKey = 'belowThreshold' | 'equalOrAboveThreshold';

export type ProductDetailFreeDeliveryMessage = {
  fontColor?: string;
  productText: string;
  showTruckIcon: boolean;
};

export type AdditionalProduct = {
  productCode: string;
  tooltip?: string;
};

export type AdditionalProductsData = {
  products: Array<ProductDetail['productCode']>;
  buyingGroupLevel4: Array<ProductDetail['buyingGroup']>;
  title?: string;
  additionalProducts: Array<AdditionalProduct>;
};

export type ProductDetailV2Props<ReviewType = Record<string, unknown>> = ProductDetailV2Options & {
  productManual?: ProductManual;
  productReview?: ReviewType;
  productVideos?: Array<ProductVideo>;
  productLD?: Product;
  renderSizeButtons?: boolean;
  inANutshell?: {
    path: string;
  };
  freeDeliveryMessage: Record<ProductDetailFreeDeliveryMessageKey, ProductDetailFreeDeliveryMessage>;
  thresholdFreeDelivery?: number;
  additionalProductSelector: ProductAdditionalProductSelector;
};

export type HelpCenterProps = {
  targetUrl?: string;
  targetText?: string;
  image?: ImagePathWithRenditions;
  phoneLink?: string;
  phoneTitle?: string;
  workingHours?: string;
  title?: string;
};

export type ProductSizeRecommendation = {
  modelSize: string;
  modelHeight: string;
  sizingAdvice: string;
};

export type ProductSizeRecommendationData = {
  productBased: ProductSizeRecommendation | null;
  colorBased: ProductSizeRecommendation | null;
};

export type ProductImagesByColor = {
  colorId: number;
  images: Array<ProductColorVariationImage>;
};

export type ProductTechnicalCategory = {
  categoryCode: string;
  categoryId: number;
  categoryName: string;
  categoryRank: number;
  features: Array<ProductTechnicalCategoryFeature>;
};

export type ProductTechnicalCategoryFeature = {
  featureId: number;
  featureCode: string;
  featureType: string;
  featureName: string;
  featureValue: string;
  featureRank: number;
};

export type LinkedProduct = {
  productId: number;
  market: string;
  mainWebShopId: number;
  productColourId: number;
  fictionalShopId: number;
  linkedProductId: number;
  linkedProductCode: string;
  linkedProductColourId: number;
  linkedColourId: number;
  linkedFictionalShopId: number;
  linkTypeId: number;
  linkTypeCode: ProductLinkType;
  active: boolean;
};

export type ProductFulfillmentBySize = {
  storeSourceExcluded?: boolean;
  fulfillmentCopy?: Record<string, string>;
};

export type ProductFeatureRangeValue = { min: number; max: number };

export type ProductFeature = {
  featureName?: string;
  featureId: number;
  title: string;
  description: string;
  icon: string;
  featureValue: boolean | string | Partial<ProductFeatureRangeValue>;
};

export interface InANutshellFeature extends ProductFeature {
  buyingGroups: Array<string>;
  mutualExclusiveInANutshellFeatureIds: Array<number>;
  matchType: 'boolean' | 'exact' | 'range';
}

export type ProductExclusion = {
  skuId: number;
  storeSourceExcluded: boolean;
  fulfillmentCopy: Record<string, string>;
};

export type ProductCodeVariant = {
  colorId?: string;
  productCode: string;
  variantValue: string;
  productSeoUrl: string;
};

export type ProductVariant = {
  variantType: string;
  productCodeVariants: Array<ProductCodeVariant>;
};

export type ProductSpecialty = {
  webShopId: string;
  market: string;
  buyingGroup: string;
  specialtyType: ProductSpecialtyType;
};

export type ProductColorVariationWithoutAvailabilities = {
  visible: boolean;
  active: boolean;
  webOnly: boolean;
  ccExcluded: boolean;
  colorId: number;
  color: string;
  colorCode: string;
  translations: Record<SupportedLanguages, ProductColorVariationTranslation>;
  images: Array<ProductColorVariationImage>;
  swatch: string;
  defaultImage?: string;
  sizes: Array<ProductColorVariationSize>;
  colourDiscountPercentage: number;
  colourDiscountAmount: number;
  newProduct?: boolean;
  productColorId?: number;
};

export type ProductSustainableCertificationProps = {
  data: {
    sustainableCertificationList: {
      items: Array<ProductSustainableCertificationItemProps>;
    };
  };
};

export type ProductSustainableCertificationItemProps = {
  productCode: string;
  buyingGroup: string | null;
  certificateType: string;
  certificateTitleText: string;
  certificateContentText: string;
  _tags: Array<string>;
  _variation: string;
};

export type ProductDetailWithoutAvailabilities = {
  active: boolean;
  visible: boolean;
  productId: string;
  productCode: string;
  mainWebShop: string;
  productTypeFeatureId: string;
  productQualifier: string;
  gender: string;
  genderFeatureId: string;
  brandId: string;
  brandQualifier: string;
  defaultCategoryId: string;
  fictionalId: string;
  seoUrl: string;
  careFeature: string;
  defaultColor: number;
  defaultImage: string;
  productColorVariations: Array<ProductColorVariationWithoutAvailabilities>;
  productTranslatedProperties: Record<SupportedLanguages, ProductTranslatedProperty>;
  webOnly: boolean;
  buyingGroup: string;
  showSizeGuide: boolean;
  thumbSplit: boolean;
  showQuantity: boolean;
  showColorSwatches: boolean;
  productAvailability: number;
  colourAvailability?: number;
  skuAvailability?: number;
  sustainable?: boolean;
  ourPlanet?: boolean;
  buyingGroupLevel3Code?: string;
};

export type ProductAdditionalProduct = {
  id: string;
  tooltip?: string;
  product: ProductLister &
    ProductDetailWithoutAvailabilities &
    ProductAvailability &
    ProductDetailPriceData & { productTranslatedProperties: ProductTranslatedProperty };
  displayedColor: ProductColor;
};

export type ProductAdditionalProductSelector = {
  title?: string;
  additionalProducts: Array<ProductAdditionalProduct>;
};

export type ProductSizeLength = 'Short' | 'Regular' | 'Long' | 'Extra Long';
